import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PermissionDenialService } from '../../../core/services/permission-denial.service';
import {
  describePermission, ASK_ADMIN_NOTE, denialHeadlineFor, denialNextStep,
  friendlyDenialSentence,
} from '../../../core/utils/permission-denial.utils';
import { PermissionService } from '../../../core/services/permission.service';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

/**
 * v1.6.1687 — Asana 1216892546346406. The one notice for every permission 403
 * on the current page.
 *
 * Mounted once in the app shell, it covers every screen without each one
 * needing markup — which is the point of handling the 403 centrally. A screen
 * that wants a denial shown INSIDE a specific region (its empty list, its
 * store filter) can also mount it with `[forUrl]` and `inline`.
 *
 * `showAdminNote` defaults on: the operator cannot fix this themselves, so the
 * only useful next step is who to ask.
 */
@Component({
  selector: 'app-permission-notice',
  standalone: true,
  imports: [CommonModule],
  template: `
<!-- v1.6.1766 — the "mb-4" lives HERE, not on the host (Asana 1219657717405746).
     A host class applies whether or not the component renders anything, so on
     the app shell it was a permanent 16px gap above every page title. On the
     inner div it exists only when there is a notice to separate from the
     page. -->
<!-- v1.6.1814 — exports switched off by the platform team (Asana
     1219882772481516). Its own block, NOT folded into the permission notice:
     that one offers "ask an administrator to add the permission to your
     role", which is wrong here — no permission is missing. The message is the
     server's, shown as sent, because it names who can switch exports back
     on. A plug icon rather than a lock, so the two read as different things
     at a glance. -->
<div *ngIf="!forUrl && exportSwitchNotice"
     class="flex items-start gap-3 rounded-xl border px-4 py-3 mb-4 bg-slate-50 border-slate-200">
  <i class="fa-solid fa-circle-minus text-slate-400 mt-0.5 shrink-0"></i>
  <p class="text-sm text-slate-800 m-0 min-w-0 flex-1">{{ exportSwitchNotice }}</p>
</div>

<div *ngIf="visible"
     class="flex items-start gap-3 rounded-xl border px-4 py-3 mb-4"
     [ngClass]="inline ? 'bg-amber-50 border-amber-200' : 'bg-amber-50 border-amber-200'">
  <i class="fa-solid fa-lock text-amber-500 mt-0.5 shrink-0"></i>
  <div class="min-w-0 flex-1">
    <!-- The server's own sentence, shown as sent: it already names the missing
         permission and the ticket says not to write our own text. -->
    <p class="text-sm text-amber-900 m-0">{{ headline }}</p>

    <!-- The keys as chips, from errors.required_permissions. Each shows a
         readable description AND the raw key, because the raw key is what an
         administrator types into the role editor.

         v1.6.1771 — and ONLY an administrator sees them. To everyone else a
         bare permission key reads as a crash, which is Asana 1219658573529681.
         The sentence above already names what was blocked, so nothing is lost
         by hiding them from the people who cannot act on them. -->
    <div *ngIf="permissions.length && isAdmin" class="flex flex-wrap gap-1.5 mt-2">
      <span *ngFor="let p of permissions"
            class="inline-flex items-baseline gap-1.5 px-2 py-0.5 rounded bg-amber-100 text-amber-900 text-[11px]">
        <span class="font-semibold">{{ describe(p) }}</span>
        <code class="font-mono text-[10px] text-amber-700">{{ p }}</code>
      </span>
    </div>

    <p *ngIf="showAdminNote" class="text-xs text-amber-800 m-0 mt-2">{{ nextStep }}</p>

    <!-- v1.6.1771 — Asana 1219658573529681: a way out. The page behind this
         notice is empty, and until now the only move was the browser's back
         button. Shown on the full-page notice only; inline, the rest of the
         page still works and there is nothing to leave. -->
    <div *ngIf="!inline" class="flex items-center gap-2 mt-3">
      <button type="button" (click)="goBack()"
              class="px-3 py-1.5 rounded-lg border border-amber-300 bg-white text-amber-900 text-xs font-semibold hover:bg-amber-50 transition">
        <i class="fa-solid fa-arrow-left text-[10px] mr-1.5"></i>Go back
      </button>
      <button type="button" (click)="goDashboard()"
              class="px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-800 hover:text-amber-900 hover:underline transition">
        Return to dashboard
      </button>
    </div>
  </div>
</div>
  `,
})
export class PermissionNoticeComponent {
  private denials = inject(PermissionDenialService);
  private perms = inject(PermissionService);
  private router = inject(Router);
  private location = inject(Location);

  /**
   * Limit this notice to the denial for one request, by URL fragment.
   *
   * This is how a screen tells its MAIN request from a side one: the main
   * request's denial replaces the list, while a side request's (the store
   * filter, say) shows only on that part and leaves the rest working.
   */
  @Input() forUrl = '';
  @Input() inline = false;
  @Input() showAdminNote = true;

  get visible(): boolean {
    return this.forUrl ? this.denials.wasDenied(this.forUrl) : this.denials.hasDenials();
  }

  /**
   * v1.6.1771 — the sentence depends on who is reading.
   *
   * An administrator keeps the server's own wording: it is precise, and it
   * matches what they will find in the logs. Everyone else gets a sentence
   * about the page they tried to open, because the server's version names a
   * permission key they cannot act on.
   */
  get headline(): string {
    if (this.forUrl) {
      const d = this.denials.forUrl(this.forUrl);
      if (!d) return 'You do not have permission to see this.';
      if (this.isAdmin) return d.message;
      return friendlyDenialSentence(d.permissions) || d.message;
    }
    return denialHeadlineFor(this.denials.denials(), this.isAdmin) || this.denials.headline();
  }

  /** v1.6.1814 — the export-switch refusal for this page, if any. */
  get exportSwitchNotice(): string { return this.denials.exportSwitchNotice(); }

  get isAdmin(): boolean { return this.perms.isSuperAdmin(); }
  get nextStep(): string { return denialNextStep(this.isAdmin); }

  goBack(): void { this.location.back(); }
  goDashboard(): void { this.router.navigate(['/dashboard']); }

  get permissions(): string[] {
    if (this.forUrl) return this.denials.forUrl(this.forUrl)?.permissions ?? [];
    return this.denials.missingPermissions();
  }

  readonly ASK_ADMIN_NOTE = ASK_ADMIN_NOTE;
  describe(p: string): string { return describePermission(p); }
}
